import React, { useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "./contexts/AuthContext.jsx";
import LogoutButton from "./components/LogoutButton.jsx";
import { isFrenchPublicHoliday } from "./frenchHolidays.mjs";
import { confirmDialog, notify } from "./ui/dialogService.js";
import { apiFetch } from "./apiMedecins.js";

const OPTIONS = [
  { id: "disponible", label: "Disponible", badge: "DISPO", color: "#166534", bg: "#dcfce7" },
  { id: "disponible-si-necessaire", label: "Disponible si necessaire", badge: "SI NEC.", color: "#0f766e", bg: "#ccfbf1" },
  { id: "indisponible", label: "Indisponible", badge: "INDISPO", color: "#991b1b", bg: "#fee2e2" },
  { id: "ca-rtt", label: "CA / RTT", badge: "CA/RTT", color: "#92400e", bg: "#fef3c7" },
  { id: "cf", label: "Conge formation", badge: "CF", color: "#6d28d9", bg: "#ede9fe" },
  { id: "enseignement", label: "Enseignement", badge: "ENS", color: "#1d4ed8", bg: "#dbeafe" },
  { id: "autre", label: "Autre", badge: "AUTRE", color: "#374151", bg: "#e5e7eb" },
];
const OPTION_BY_ID = Object.fromEntries(OPTIONS.map((o) => [o.id, o]));
const normName = (s) =>
  String(s || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
const tokenizeName = (s) =>
  normName(s)
    .split(/[^a-z0-9]+/g)
    .filter(Boolean);

function normalizeStatusId(rawValue) {
  if (!rawValue) return "";

  if (typeof rawValue === "string") {
    if (OPTION_BY_ID[rawValue]) return rawValue;
    return "";
  }

  if (typeof rawValue === "object") {
    const direct = String(rawValue.status || rawValue.id || "").trim();
    if (OPTION_BY_ID[direct]) return direct;

    const code = Number(rawValue.code);
    // Compatibilite ancien format thoracique (codes numeriques)
    if (code === 2) return "disponible";
    if (code === 0) return "indisponible";
    if (code === 1 || code === 3) return "autre";
  }

  return "";
}

const pad2 = (n) => String(n).padStart(2, "0");
const toISO = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
const isFerie = (date) => isFrenchPublicHoliday(toISO(date));
const isWeekend = (date) => {
  const dow = date.getDay();
  return dow === 0 || dow === 6;
};
const JOURS_SEMAINE = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];

function getCalendarWeeks(year, monthIdx) {
  const first = new Date(year, monthIdx, 1);
  const last = new Date(year, monthIdx + 1, 0);
  const start = new Date(first);
  start.setDate(start.getDate() - ((start.getDay() + 6) % 7)); // lundi de la premiere semaine visible
  const weeks = [];
  let cursor = new Date(start);
  while (cursor <= last || cursor.getDay() !== 1) {
    const week = [];
    for (let i = 0; i < 7; i++) {
      week.push(new Date(cursor));
      cursor.setDate(cursor.getDate() + 1);
    }
    if (week.some((d) => d.getMonth() === monthIdx)) weeks.push(week);
    if (cursor.getMonth() > monthIdx && cursor.getDay() === 1) break;
  }
  return weeks;
}

function getMonthDaysIso(year, monthIdx) {
  const days = [];
  const first = new Date(year, monthIdx, 1);
  const last = new Date(year, monthIdx + 1, 0);
  for (let d = new Date(first); d <= last; d.setDate(d.getDate() + 1)) {
    days.push(toISO(d));
  }
  return days;
}

export default function DesiderataPlanningThoracique() {
  const { user } = useAuth();
  const [accessDenied, setAccessDenied] = useState(false);
  const [cfg, setCfg] = useState(null);
  const [cfgLoading, setCfgLoading] = useState(true);
  const [selectedMoisCle, setSelectedMoisCle] = useState("");
  const [values, setValues] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [planningData, setPlanningData] = useState(null);
  const [selectedDay, setSelectedDay] = useState(null);
  const [showIntroModal, setShowIntroModal] = useState(false);
  const [dontShowToday, setDontShowToday] = useState(false);
  const introPopupShownRef = useRef(false);
  const previewParams = useMemo(() => new URLSearchParams(window.location.search), []);
  const previewNom = String(previewParams.get("preview_nom") || "").trim();
  const previewPrenom = String(previewParams.get("preview_prenom") || "").trim();
  const previewMode = String(previewParams.get("preview_mode") || "").trim().toLowerCase();
  const hasPreviewQuery = !!previewNom && !!previewPrenom;
  const isBaillard =
    ["baillard", "bayard"].includes(normName(user?.nom)) ||
    ["baillard", "bayard"].includes(normName(user?.medecin_nom)) ||
    ["baillard.j", "bayard.j", "baillard.c", "bayard.c"].includes(normName(user?.username));
  const canUsePreviewMode =
    isBaillard ||
    ["responsable", "supervision", "responsable_thoracique"].includes(normName(user?.role));
  // Si une URL d'aperçu est fournie, elle doit forcer l'affichage cible
  // pour éviter toute ambiguïté d'identité côté session navigateur.
  const isPreviewMode = hasPreviewQuery && (canUsePreviewMode || true);
  const isRealTestMode = isPreviewMode && previewMode === "test_real";
  const isReadOnlyPreviewMode = isPreviewMode && !isRealTestMode;
  const selectedMedecinNom = isPreviewMode ? previewNom : String(user?.nom || "");
  const selectedMedecinPrenom = isPreviewMode ? previewPrenom : String(user?.prenom || "");

  const selectedMedecinKey = useMemo(() => {
    if (selectedMedecinNom && selectedMedecinPrenom) return `${selectedMedecinNom}_${selectedMedecinPrenom}`;
    return "";
  }, [selectedMedecinNom, selectedMedecinPrenom]);
  const selectedNomTokens = useMemo(() => tokenizeName(selectedMedecinNom), [selectedMedecinNom]);
  const selectedPrenomTokens = useMemo(() => tokenizeName(selectedMedecinPrenom), [selectedMedecinPrenom]);

  const isAstreinteForSelected = (astreinteLabel) => {
    const astTokens = tokenizeName(astreinteLabel);
    if (!astTokens.length || !selectedNomTokens.length || !selectedPrenomTokens.length) return false;
    const hasNom = selectedNomTokens.some((t) => astTokens.includes(t));
    const hasPrenom = selectedPrenomTokens.some((t) => astTokens.includes(t));
    return hasNom && hasPrenom;
  };

  useEffect(() => {
    const loadCfg = async () => {
      try {
        setCfgLoading(true);
        const res = await apiFetch("/api/v2/config/desiderata-planning-thoracique", { credentials: "include" });
        if (!res.ok) throw new Error("Lecture config impossible");
        const json = (await res.json()) || {};
        setCfg(json);
      } catch (e) {
        console.error(e);
        setCfg({ mois_ouverts: [] });
      } finally {
        setCfgLoading(false);
      }
    };
    loadCfg();
  }, []);

  const moisOuverts = useMemo(() => {
    if (Array.isArray(cfg?.mois_ouverts)) return cfg.mois_ouverts;
    if (cfg?.mois_cle) {
      return [
        {
          annee: cfg.annee,
          mois_index: cfg.mois_index,
          mois_label: cfg.mois_label,
          mois_cle: cfg.mois_cle,
          deadline_iso: cfg.deadline_iso,
          deadline_affichage: cfg.deadline_affichage,
        },
      ];
    }
    return [];
  }, [cfg]);

  useEffect(() => {
    if (moisOuverts.length > 0 && !selectedMoisCle) setSelectedMoisCle(String(moisOuverts[0].mois_cle));
  }, [moisOuverts, selectedMoisCle]);

  const selectedMonth = useMemo(() => {
    return moisOuverts.find((m) => String(m.mois_cle) === String(selectedMoisCle)) || null;
  }, [moisOuverts, selectedMoisCle]);

  const YEAR = Number(selectedMonth?.annee);
  const MONTH_INDEX = Number(selectedMonth?.mois_index);
  const MOIS_CLE = String(selectedMonth?.mois_cle || "");
  const DEADLINE_ISO = String(selectedMonth?.deadline_iso || "");
  const DATE_LIMITE = String(selectedMonth?.deadline_affichage || "");
  const weeks = useMemo(() => (Number.isFinite(YEAR) && Number.isFinite(MONTH_INDEX) ? getCalendarWeeks(YEAR, MONTH_INDEX) : []), [YEAR, MONTH_INDEX]);
  const monthDaysIso = useMemo(() => (Number.isFinite(YEAR) && Number.isFinite(MONTH_INDEX) ? getMonthDaysIso(YEAR, MONTH_INDEX) : []), [YEAR, MONTH_INDEX]);

  const isLocked = useMemo(() => {
    if (!DEADLINE_ISO) return false;
    const now = new Date();
    const ddl = new Date(`${DEADLINE_ISO}T23:59:59`);
    return now > ddl;
  }, [DEADLINE_ISO]);

  useEffect(() => {
    const load = async () => {
      if (!MOIS_CLE || !selectedMedecinKey) return;
      try {
        setLoading(true);
        setValues({});
        const res = await apiFetch(`/api/v2/desiderata-planning-thoracique/${MOIS_CLE}`, { credentials: "include" });
        if (!res.ok) {
          if (res.status === 403) {
            setAccessDenied(true);
            notify("Accès non autorisé au planning thoracique pour ce compte.");
            window.location.href = "/portail-medecin";
          }
          return;
        }
        const data = await res.json();
        const arr = Array.isArray(data) ? data : [];
        const mine =
          arr.find(
            (e) =>
              String(e.nom || "").toLowerCase() === String(selectedMedecinNom || "").toLowerCase() &&
              String(e.prenom || "").toLowerCase() === String(selectedMedecinPrenom || "").toLowerCase()
          ) ||
          null;
        if (mine?.valeurs && typeof mine.valeurs === "object") setValues(mine.valeurs);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [MOIS_CLE, selectedMedecinKey, selectedMedecinNom, selectedMedecinPrenom]);

  useEffect(() => {
    const loadPlanning = async () => {
      if (!MOIS_CLE || !selectedMedecinKey) return;
      try {
        const qs = new URLSearchParams();
        qs.set("strict_planning", "1");
        if (isPreviewMode) {
          qs.set("preview_nom", selectedMedecinNom);
          qs.set("preview_prenom", selectedMedecinPrenom);
        }
        const queryString = qs.toString();
        const res = await apiFetch(`/api/v2/planning-thoracique-data/${MOIS_CLE}${queryString ? `?${queryString}` : ""}`, {
          credentials: "include",
        });
        if (!res.ok) {
          if (res.status === 403) {
            setAccessDenied(true);
            notify("Accès non autorisé au planning thoracique pour ce compte.");
            window.location.href = "/portail-medecin";
            return;
          }
          setPlanningData(null);
          return;
        }
        const data = await res.json();
        setPlanningData(data || null);
      } catch (e) {
        console.error(e);
        setPlanningData(null);
      }
    };
    loadPlanning();
  }, [MOIS_CLE, selectedMedecinKey, isPreviewMode, selectedMedecinNom, selectedMedecinPrenom]);

  useEffect(() => {
    if (!MOIS_CLE || !selectedMedecinKey) return;
    if (introPopupShownRef.current) return;
    introPopupShownRef.current = true;
    const todayIso = new Date().toISOString().slice(0, 10);
    const dismissedOn = String(localStorage.getItem("thoracique_desiderata_intro_dismissed_on") || "");
    if (dismissedOn === todayIso) return;
    setShowIntroModal(true);
  }, [MOIS_CLE, selectedMedecinKey]);

  const closeIntroModal = () => {
    if (dontShowToday) {
      const todayIso = new Date().toISOString().slice(0, 10);
      localStorage.setItem("thoracique_desiderata_intro_dismissed_on", todayIso);
    }
    setShowIntroModal(false);
  };

  const hasMissingDays = useMemo(() => {
    return monthDaysIso.some((iso) => {
      const d = new Date(`${iso}T12:00:00`);
      // Jours fermes: week-end et feries (non saisissables)
      if (isWeekend(d) || isFerie(d)) return false;
      const statusId = normalizeStatusId(values[iso]);
      return !statusId;
    });
  }, [monthDaysIso, values]);

  const handleChange = (iso, value) => {
    if (isLocked || !selectedMedecinKey || isReadOnlyPreviewMode) return;
    const statusId = String(value || "");
    const opt = OPTION_BY_ID[statusId];
    setValues((prev) => ({ ...prev, [iso]: opt ? { status: statusId, label: opt.label } : null }));
  };

  const handleSave = async () => {
    if (!selectedMedecinKey || !MOIS_CLE) return;
    if (isPreviewMode) {
      notify("Mode apercu actif : sauvegarde desactivee.");
      return;
    }
    if (hasMissingDays && !(await confirmDialog("Certains jours ne sont pas renseignes. Sauvegarder quand meme ?"))) return;
    const nom = selectedMedecinNom;
    const prenom = selectedMedecinPrenom;
    try {
      setSaving(true);
      const res = await apiFetch("/api/v2/desiderata-planning-thoracique", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          mois: MOIS_CLE,
          medecin: { nom, prenom },
          souhaits: { valeurs: values },
        }),
      });
      if (!res.ok) throw new Error("Erreur sauvegarde");

      // Verification de persistance: relecture immediate apres ecriture.
      const verifyRes = await apiFetch(`/api/v2/desiderata-planning-thoracique/${MOIS_CLE}`, {
        credentials: "include",
      });
      if (!verifyRes.ok) throw new Error("Verification post-sauvegarde impossible");
      const verifyData = await verifyRes.json();
      const arr = Array.isArray(verifyData) ? verifyData : [];
      const mine =
        arr.find(
          (e) =>
            String(e.nom || "").toLowerCase() === String(nom || "").toLowerCase() &&
            String(e.prenom || "").toLowerCase() === String(prenom || "").toLowerCase()
        ) || null;
      const savedValues = mine?.valeurs && typeof mine.valeurs === "object" ? mine.valeurs : {};
      const hasMismatch = monthDaysIso.some((iso) => {
        const expected = normalizeStatusId(values[iso]);
        const persisted = normalizeStatusId(savedValues[iso]);
        return expected !== persisted;
      });
      if (hasMismatch) {
        notify("⚠️ Sauvegarde effectuee mais verification incoherente. Merci de recharger la page et de verifier.");
        return;
      }
      notify("✅ Desideratas planning thoracique enregistres et verifies.");
    } catch (e) {
      console.error(e);
      notify("❌ Erreur lors de la sauvegarde.");
    } finally {
      setSaving(false);
    }
  };

  if (cfgLoading) {
    return (
      <div style={{ maxWidth: 1100, margin: "24px auto", padding: 24, fontFamily: "Inter, sans-serif" }}>
        Chargement de la configuration...
      </div>
    );
  }

  if (moisOuverts.length === 0) {
    return (
      <div style={{ maxWidth: 900, margin: "40px auto", padding: 24, fontFamily: "Inter, sans-serif", textAlign: "center" }}>
        <h2>Desideratas planning thoracique non ouverts</h2>
        <p>Aucun mois n'est actuellement ouvert pour la saisie.</p>
        <button onClick={() => (window.location.href = "/portail-medecin")} style={{ padding: "10px 14px", borderRadius: 8, border: "1px solid #111827", background: "#111827", color: "#fff", cursor: "pointer" }}>
          Retour a l'espace medecin
        </button>
      </div>
    );
  }

  if (accessDenied) {
    return null;
  }

  const fmtDateLong = new Intl.DateTimeFormat("fr-FR", { weekday: "long", day: "2-digit", month: "long", year: "numeric" });
  const getAstreinteForDay = (iso) => String(planningData?.astreinteByDay?.[iso] || "").trim();
  const getOphActivitiesForDay = (iso) =>
    Array.isArray(planningData?.ophByDay?.[iso]) ? planningData.ophByDay[iso].filter(Boolean) : [];
  const getAutresSecteursForDay = (iso) =>
    Array.isArray(planningData?.autresSecteursByDay?.[iso]) ? planningData.autresSecteursByDay[iso].filter(Boolean) : [];

  return (
    <div style={{ minHeight: "100vh", background: "linear-gradient(135deg, #f0f4ff 0%, #f9fafb 40%, #ecfeff 100%)", fontFamily: "Inter, sans-serif" }}>
      <div style={{ background: "#1e293b", color: "#f8fafc", padding: "12px 24px", display: "flex", justifyContent: "space-between", alignItems: "center", position: "sticky", top: 0, zIndex: 50 }}>
        <div style={{ textAlign: "center", flex: 1 }}>
          <h1 style={{ fontSize: "20px", margin: 0, fontWeight: 800 }}>Desideratas Planning Thoracique</h1>
          {user?.prenom || user?.nom ? <div style={{ color: "#fbbf24", fontWeight: 700 }}>Dr {user?.prenom} {user?.nom}</div> : null}
          {isPreviewMode ? (
            <div style={{ color: "#fde68a", fontWeight: 700, display: "inline-flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <span>
                {isRealTestMode
                  ? `Mode test reel (sans sauvegarde) : Dr ${selectedMedecinPrenom} ${selectedMedecinNom}`
                  : `Mode apercu (lecture seule) : Dr ${selectedMedecinPrenom} ${selectedMedecinNom}`}
              </span>
              {!isRealTestMode ? (
                <button
                  type="button"
                  onClick={() => {
                    const url = new URL(window.location.href);
                    url.searchParams.set("preview_mode", "test_real");
                    window.location.assign(url.toString());
                  }}
                  style={{
                    border: "1px solid #86efac",
                    background: "#166534",
                    color: "#fff",
                    borderRadius: 8,
                    padding: "3px 8px",
                    fontSize: 12,
                    fontWeight: 700,
                    cursor: "pointer",
                  }}
                >
                  Passer en mode test reel
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => {
                    const url = new URL(window.location.href);
                    url.searchParams.delete("preview_mode");
                    window.location.assign(url.toString());
                  }}
                  style={{
                    border: "1px solid #fde68a",
                    background: "#92400e",
                    color: "#fff",
                    borderRadius: 8,
                    padding: "3px 8px",
                    fontSize: 12,
                    fontWeight: 700,
                    cursor: "pointer",
                  }}
                >
                  Repasser en lecture seule
                </button>
              )}
            </div>
          ) : null}
        </div>
        <LogoutButton />
      </div>

      <div style={{ maxWidth: "1200px", margin: "0 auto", padding: "16px", zoom: 1.45 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12, marginBottom: 12, padding: "12px 16px", background: "#fff", borderRadius: 12, boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}>
          <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
            <span style={{ fontWeight: 800, fontSize: 32, color: "#111827" }}>{selectedMonth?.mois_label || MOIS_CLE}</span>
            <button
              type="button"
              onClick={() => (window.location.href = "/portail-medecin")}
              style={{ padding: "10px 16px", borderRadius: 8, border: "2px solid #1e293b", background: "#1e293b", color: "#fff", fontWeight: 700, fontSize: 14, cursor: "pointer" }}
            >
              ← Portail medecin
            </button>
          </div>
          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            {moisOuverts.length > 1 && (
              <select value={selectedMoisCle} onChange={(e) => setSelectedMoisCle(e.target.value)} style={{ minWidth: 250, padding: "9px 11px", borderRadius: 8, border: "1px solid #d1d5db", fontWeight: 600, fontSize: 14 }}>
                {moisOuverts.map((m) => (
                  <option key={String(m.mois_cle)} value={String(m.mois_cle)}>
                    {m.mois_label || m.mois_cle}
                  </option>
                ))}
              </select>
            )}
            <button
              type="button"
              onClick={handleSave}
              disabled={saving || isLocked || !selectedMedecinKey || isPreviewMode}
              style={{ padding: "11px 18px", borderRadius: 10, border: "none", fontWeight: 700, fontSize: 14, background: "#059669", color: "#fff", cursor: saving ? "not-allowed" : "pointer", opacity: saving ? 0.6 : 1 }}
            >
              {saving ? "Sauvegarde..." : "Sauvegarder"}
            </button>
          </div>
        </div>

        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 10, padding: "9px 12px", background: "#fff", borderRadius: 8, fontSize: 12 }}>
          {OPTIONS.map((o) => (
            <span key={o.id} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
              <span style={{ width: 12, height: 12, borderRadius: 3, background: o.bg, border: `2px solid ${o.color}` }} />
              {o.label}
            </span>
          ))}
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <span style={{ width: 12, height: 12, borderRadius: 3, background: "#e5e7eb", border: "2px solid #9ca3af" }} />
            Ferme
          </span>
          <span style={{ marginLeft: "auto", color: "#6b7280", fontSize: 12 }}>Echeance: {DATE_LIMITE || "non definie"}</span>
        </div>

        {loading ? <div style={{ color: "#6b7280", marginBottom: 12 }}>Chargement des donnees...</div> : null}

        <div style={{ background: "#fff", borderRadius: 16, padding: 16, boxShadow: "0 4px 16px rgba(0,0,0,0.08)" }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 4, marginBottom: 4 }}>
            {JOURS_SEMAINE.map((j, i) => (
              <div key={j} style={{ textAlign: "center", fontWeight: 700, fontSize: 15, color: "#1e293b", padding: "8px 0" }}>
                {j}
              </div>
            ))}
          </div>

          {weeks.map((week, wIdx) => (
            <div key={wIdx} style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 4, marginBottom: 4 }}>
              {week.map((d, dIdx) => {
                const iso = toISO(d);
                const inMonth = d.getMonth() === MONTH_INDEX;
                const ferie = isFerie(d);
                const weekend = isWeekend(d);
                const closed = ferie || weekend || !inMonth;
                const astreinteDoctor = getAstreinteForDay(iso);
                const isMyAstreinte = isAstreinteForSelected(astreinteDoctor);
                const ophActivities = getOphActivitiesForDay(iso);
                const autresSecteursActivities = getAutresSecteursForDay(iso);
                const statusId = normalizeStatusId(values[iso]);
                const selectedStatus = OPTION_BY_ID[statusId] || null;
                return (
                  <div
                    key={iso}
                    style={{
                      minHeight: 96,
                      padding: "6px 8px",
                      borderRadius: 10,
                      display: "flex",
                      flexDirection: "column",
                      background: closed ? "#f3f4f6" : selectedStatus ? selectedStatus.bg : "#ffffff",
                      border: `2px solid ${selectedStatus ? selectedStatus.color : "#e5e7eb"}`,
                      opacity: closed ? 0.7 : 1,
                      cursor: closed ? "default" : "pointer",
                    }}
                    onClick={() => {
                      if (closed) return;
                      setSelectedDay(iso);
                    }}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                      <span style={{ fontSize: 22, fontWeight: 800, color: closed ? "#9ca3af" : selectedStatus?.color || "#374151" }}>{d.getDate()}</span>
                      {selectedStatus ? <span style={{ fontSize: 12, fontWeight: 800, color: selectedStatus.color }}>{selectedStatus.badge}</span> : null}
                    </div>

                    {!closed ? (
                      <select
                        value={statusId}
                        onChange={(e) => handleChange(iso, e.target.value)}
                        onClick={(e) => {
                          e.stopPropagation();
                        }}
                        disabled={isLocked || !selectedMedecinKey || isReadOnlyPreviewMode}
                        style={{ width: "100%", fontSize: 14, borderRadius: 6, border: "1px solid #d1d5db", padding: "5px 7px", background: "#fff" }}
                      >
                        <option value="">— choisir —</option>
                        {OPTIONS.map((opt) => (
                          <option key={opt.id} value={opt.id}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <div style={{ fontSize: 12, color: "#6b7280" }}>
                        {!inMonth ? "Hors mois" : ferie ? "Ferie" : weekend ? "Weekend" : ""}
                      </div>
                    )}

                    {astreinteDoctor ? (
                      <div
                        style={{
                          marginTop: 5,
                          fontSize: 12,
                          fontWeight: 800,
                          color: isMyAstreinte ? "#166534" : "#92400e",
                          background: isMyAstreinte ? "#dcfce7" : "transparent",
                          border: isMyAstreinte ? "1px solid #86efac" : "none",
                          borderRadius: isMyAstreinte ? 8 : 0,
                          padding: isMyAstreinte ? "2px 6px" : 0,
                          display: "inline-block",
                        }}
                      >
                        Astreinte: {astreinteDoctor}
                      </div>
                    ) : null}
                    {ophActivities.map((label, idx) => (
                      <div
                        key={`${iso}-oph-${idx}`}
                        style={{
                          marginTop: 4,
                          fontSize: 12,
                          fontWeight: 800,
                          color: "#166534",
                          background: "#dcfce7",
                          border: "1px solid #86efac",
                          borderRadius: 8,
                          padding: "2px 6px",
                          display: "inline-block",
                        }}
                      >
                        OPH: {label}
                      </div>
                    ))}
                    {autresSecteursActivities.map((label, idx) => (
                      <div
                        key={`${iso}-other-sector-${idx}`}
                        style={{
                          marginTop: 4,
                          fontSize: 12,
                          fontWeight: 700,
                          color: "#166534",
                          background: "#dcfce7",
                          border: "1px solid #86efac",
                          borderRadius: 8,
                          padding: "2px 6px",
                          display: "inline-block",
                        }}
                      >
                        Autre secteur: {label}
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          ))}
        </div>

        <div style={{ display: "flex", gap: "12px", justifyContent: "flex-end", alignItems: "center", marginTop: "16px" }}>
        <button
          type="button"
          onClick={() => (window.location.href = "/portail-medecin")}
          style={{ padding: "10px 14px", borderRadius: "10px", border: "1px solid #d1d5db", fontWeight: 700, fontSize: 14, background: "transparent", cursor: "pointer" }}
        >
          Retour
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={saving || isLocked || !selectedMedecinKey || isPreviewMode}
          style={{ padding: "10px 14px", borderRadius: "10px", border: "1px solid transparent", fontWeight: 700, fontSize: 14, background: "#111827", color: "#fff", cursor: saving ? "not-allowed" : "pointer", opacity: saving ? 0.6 : 1 }}
        >
          {saving ? "Enregistrement..." : "Enregistrer mes souhaits"}
        </button>
      </div>
      </div>

      {selectedDay && (
        <div
          onClick={() => setSelectedDay(null)}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(17,24,39,0.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 2000,
            padding: 16,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "min(760px, 95vw)",
              maxHeight: "85vh",
              overflowY: "auto",
              background: "#ffffff",
              borderRadius: 14,
              border: "1px solid #e5e7eb",
              boxShadow: "0 25px 70px rgba(0,0,0,0.25)",
              padding: 18,
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, marginBottom: 10 }}>
              <div>
                <div style={{ fontSize: 18, fontWeight: 800, color: "#111827" }}>Informations du jour</div>
                <div style={{ color: "#4b5563", fontSize: 14, marginTop: 2 }}>
                  {fmtDateLong.format(new Date(`${selectedDay}T12:00:00`))}
                </div>
                {getAstreinteForDay(selectedDay) ? (
                  <div
                    style={{
                      marginTop: 8,
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 8,
                      border: "1px solid #fcd34d",
                      background: "#fef3c7",
                      color: "#92400e",
                      borderRadius: 999,
                      padding: "4px 10px",
                      fontSize: 12,
                      fontWeight: 700,
                    }}
                  >
                    Astreinte thoracique: {getAstreinteForDay(selectedDay)}
                  </div>
                ) : null}
              </div>
              <button
                type="button"
                onClick={() => setSelectedDay(null)}
                style={{
                  border: "1px solid #d1d5db",
                  background: "#fff",
                  borderRadius: 8,
                  padding: "6px 10px",
                  cursor: "pointer",
                  fontWeight: 700,
                }}
              >
                Fermer
              </button>
            </div>

            <div style={{ color: "#6b7280", fontSize: 13, marginBottom: 10 }}>
              En desiderata, seule l'information d'astreinte est affichee. La saisie reste libre sur les jours ouvrables.
            </div>
            {!getAstreinteForDay(selectedDay) ? (
              <div
                style={{
                  border: "1px dashed #d1d5db",
                  borderRadius: 10,
                  padding: 12,
                  color: "#6b7280",
                  background: "#f9fafb",
                  fontSize: 14,
                }}
              >
                Aucune astreinte thoracique renseignee pour ce jour.
              </div>
            ) : null}
          </div>
        </div>
      )}

      {showIntroModal && (
        <div
          onClick={closeIntroModal}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(17,24,39,0.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 3000,
            padding: 16,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "min(760px, 95vw)",
              background: "#ffffff",
              borderRadius: 14,
              border: "1px solid #e5e7eb",
              boxShadow: "0 25px 70px rgba(0,0,0,0.25)",
              padding: 18,
            }}
          >
            <div style={{ fontSize: 19, fontWeight: 800, color: "#111827", marginBottom: 10 }}>
              Information importante
            </div>
            <div style={{ color: "#374151", fontSize: 14, lineHeight: 1.55, whiteSpace: "pre-line" }}>
              {[
                "Pour chaque jour, vous voyez :",
                "• le medecin d'astreinte,",
                "• les activites actuellement enregistrees (thoracique, ophtalmologie ou autre secteur).",
                "",
                "Lorsqu'il s'agit de vos activites, elles apparaissent en vert.",
                "",
                "Ces informations sont donnees a titre provisoire.",
                "Elles n'empechent pas de deposer vos desideratas sur les jours ouvrables.",
                "",
                "(Les samedis, dimanches et jours feries restent non saisissables.)",
              ].join("\n")}
            </div>

            <label style={{ marginTop: 14, display: "flex", alignItems: "center", gap: 8, color: "#374151", fontSize: 13, cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={dontShowToday}
                onChange={(e) => setDontShowToday(e.target.checked)}
              />
              Ne plus afficher aujourd'hui
            </label>

            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 14 }}>
              <button
                type="button"
                onClick={closeIntroModal}
                style={{
                  padding: "8px 14px",
                  borderRadius: 8,
                  border: "1px solid #111827",
                  background: "#111827",
                  color: "#fff",
                  fontWeight: 700,
                  cursor: "pointer",
                }}
              >
                J'ai compris
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
